import { z } from 'zod';
/**
 * Development identity mode: a clearly labeled local substitute for the identity provider, used to
 * exercise the UI and authorization when no Auth0 development tenant is configured.
 *
 * - It is a separate code path from Auth0. It never backs a Microsoft, Google or SSO button.
 * - platform-api registers its routes only when TRACE_ENV is development or test AND
 *   DEV_IDENTITY_ENABLED=true, and refuses to start when production has it enabled.
 * - Tokens it issues carry this issuer, which every API accepts only in development or test.
 * - Fixtures are synthetic people and organizations from the seed data.
 */
export const DEV_IDENTITY_ISSUER = 'urn:trace:dev-identity';
export const DEV_IDENTITY_SESSION_COOKIE = 'trace_dev_identity';
/** Every dev identity request must carry this header, which a cross-site form cannot set. */
export const DEV_IDENTITY_CSRF_HEADER = 'x-trace-dev-identity';
export const devFixtureSchema = z.object({
    key: z.string(),
    displayName: z.string(),
    email: z.string(),
    summary: z.string(),
});
export const devSessionRequestSchema = z.object({ fixtureKey: z.string().min(1).max(64) });
/**
 * POST /v1/dev-identity/token. With contextId the token is organization-scoped: the context must be one the
 * session's fixture holds where the request comes from (the access resolution of GET /v1/me/access, with its
 * host scope and enforced SSO), and the token then carries the Trace claims of that context (claims.ts).
 * Without contextId no organization-scoped claim is added.
 */
export const devTokenRequestSchema = z.object({
    audience: z.string().min(1).max(200),
    contextId: z.string().min(1).max(200).optional(),
});
export const devTokenResponseSchema = z.object({
    accessToken: z.string(),
    tokenType: z.literal('Bearer'),
    expiresIn: z.number().int().positive(),
});
/**
 * POST /v1/dev-identity/service-token: a client-credentials style token for the internal API (audience
 * INTERNAL_API_AUDIENCE, subject "<principal>@clients"), so a local product backend can call
 * POST /v1/internal/access/check without an Auth0 tenant. Development and test only: only for these
 * principals, only with DEV_SERVICE_SECRET_HEADER carrying the secret platform-api was started with
 * (DEV_SERVICE_TOKEN_SECRET), never from a browser, and platform-api refuses to boot with that secret
 * anywhere else. platform-api still accepts such a token only for principals in its own allowlist.
 */
export const DEV_SERVICE_PRINCIPALS = [
    'brokers-api',
    'portal-api',
    'enterprise-api',
    'intelligence-api',
    'agent-runtime',
];
export const DEV_SERVICE_SECRET_HEADER = 'x-trace-dev-service-secret';
export const devServiceTokenRequestSchema = z.object({ principal: z.string().min(1).max(128) });
//# sourceMappingURL=dev-identity.js.map